import { DownloadEventData, MessagingService } from '../services/MessagingService/types';
import BackgroundTask from './BackgroundTask';

export type DownloadContentType = 'zip' | 'psd';
export type DownloadTaskStatus =
	| 'unknown'
	| 'processing'
	| 'successful'
	| 'failed'
	| 'canceled';

export type DownloadInitializer = ( url: string ) => void;

export type DownloadTaskParams = {
	id: string,
	messagingService: MessagingService,
	downloadInitializer: DownloadInitializer
};

export default abstract class DownloadTask implements BackgroundTask {
	id: string;
	messagingService: MessagingService;
	downloadInitializer: DownloadInitializer;

	count: number;
	total: number;
	status: DownloadTaskStatus;
	isObservingDownload: boolean;
	createdAt: Date;

	constructor( { id, messagingService, downloadInitializer }: DownloadTaskParams ) {
		this.id = id;
		this.messagingService = messagingService;
		this.downloadInitializer = downloadInitializer;

		this.count = 0;
		this.total = 0;
		this.status = 'unknown';
		this.isObservingDownload = false;
		this.createdAt = new Date();
	}

	abstract get title(): string;
	abstract get countText(): string;

	get errors() {
		return [];
	}

	get hasErrors() {
		return false;
	}

	get cancelable() {
		return true;
	}

	get progressPercent() {
		if ( this.status === 'unknown' || this.total === 0 ) return 0;

		return ( this.count / this.total ) * 100;
	}

	get hasFinished() {
		return this.status === 'successful' || this.status === 'failed' || this.status === 'canceled';
	}

	observeDownloadFromBackend() {
		if ( this.isObservingDownload ) return;

		this.messagingService.subscribeToDownload( this.id, this.onDownloadEventReceived );
		this.isObservingDownload = true;
	}

	cancel() {
		this.status = 'canceled';
		this.stopObservingDownloadFromBackend();
	}

	onDownloadEventReceived = ( data: DownloadEventData ) => {
		const { status, count, total, url } = data;

		this.count = count;
		this.total = total;
		this.status = this.mapToAssetDownloadStatus( status );

		if ( this.hasFinished ) this.stopObservingDownloadFromBackend();
		if ( status === 'ready' ) this.downloadInitializer( url! );
	}

	private mapToAssetDownloadStatus( status: DownloadEventData[ 'status' ] ) {
		if ( status === 'ready' ) return 'successful';
		if ( status === 'error' ) return 'failed';

		return status;
	}

	private stopObservingDownloadFromBackend() {
		if ( !this.isObservingDownload ) return;

		this.messagingService.unsubscribeFromDownload( this.id );
	}
}
